import { useState } from 'react';
import { THEME_DEFINITIONS } from './PresentationBannerConstants';
import { ThemeLightbox, LightboxTheme } from './PresentationThemeLightbox';

interface Props { onSignUp: () => void; }

// Infograpia-inspired editorial color schemes — bold, high-contrast, flat-design palettes
const THEMES: LightboxTheme[] = [
  {
    name: 'Deep Navy',
    preview: 'https://public.readdy.ai/ai/img_res/6fef79427b9bfb4cf2aea4f316b87395.jpg',
    palette: ['#0A2647', '#144272', '#2C74B3'],
  },
  {
    name: 'Coral Fire',
    preview: 'https://readdy.ai/api/search-image?query=rose%20pink%20elegant%20modern%20presentation%20slide%20soft%20gradient%20feminine%20professional%20minimal%20clean%20typography&width=400&height=225&seq=style-rose&orientation=landscape',
    palette: ['#E63946', '#F4A261', '#2A9D8F'],
  },
  {
    name: 'Teal Fresh',
    preview: 'https://readdy.ai/api/search-image?query=green%20sage%20professional%20presentation%20slide%20modern%20natural%20organic%20clean%20minimal%20design%20white%20clean&width=400&height=225&seq=style-forest&orientation=landscape',
    palette: ['#264653', '#2A9D8F', '#E9C46A'],
  },
  {
    name: 'Bold Sunset',
    preview: 'https://readdy.ai/api/search-image?query=yellow%20orange%20vibrant%20energetic%20presentation%20slide%20modern%20bold%20sunny%20bright%20creative%20design%20minimal&width=400&height=225&seq=style-solar&orientation=landscape',
    palette: ['#D62828', '#F77F00', '#FCBF49'],
  },
  {
    name: 'Ink & Mint',
    preview: 'https://readdy.ai/api/search-image?query=deep%20teal%20ocean%20blue%20professional%20presentation%20slide%20modern%20corporate%20cool%20depth%20minimal%20clean%20crisp&width=400&height=225&seq=style-ocean&orientation=landscape',
    palette: ['#1D3557', '#457B9D', '#4ECDC4'],
  },
  {
    name: 'Graphite Pop',
    preview: 'https://readdy.ai/api/search-image?query=charcoal%20gray%20bold%20dramatic%20presentation%20slide%20modern%20corporate%20strong%20typography%20white%20accent%20minimal&width=400&height=225&seq=style-char&orientation=landscape',
    palette: ['#2B2D42', '#8D99AE', '#FF6B6B'],
  },
];

// Infograpia's signature accent palette — high-chroma editorial colors
const ACCENT_COLORS = ['#E63946', '#2A9D8F', '#0A2647', '#F77F00', '#7C3AED', '#264653', '#D62828', '#4ECDC4'];

// Infographic type color legend (shown in theme info bar)
const INFOGRAPHIC_TYPE_COLORS: Record<string, string> = {
  'Deep Navy':    '#144272',
  'Coral Fire':   '#E63946',
  'Teal Fresh':   '#2A9D8F',
  'Bold Sunset':  '#F77F00',
  'Ink & Mint':   '#4ECDC4',
  'Graphite Pop': '#FF6B6B',
};

export function StylePanel({ onSignUp }: Props) {
  const [selected, setSelected]           = useState('Deep Navy');
  const [selectedColor, setSelectedColor] = useState('#E63946');
  const [lightboxTheme, setLightboxTheme] = useState<LightboxTheme | null>(null);

  const active   = THEMES.find(t => t.name === selected) ?? THEMES[0];
  const themeDef = THEME_DEFINITIONS[selected] ?? THEME_DEFINITIONS['Midnight Pro'];

  return (
    <>
      <div className="grid grid-cols-2 gap-6">
        {/* Left — theme picker grid */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Theme</p>
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-full">
              <i className="ri-palette-line text-amber-400 text-xs"></i>
              <span className="text-xs font-semibold text-amber-300">Infograpia</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {THEMES.map(t => (
              <button
                key={t.name}
                onClick={() => setSelected(t.name)}
                className={`relative group rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${selected === t.name ? 'border-amber-400/70 scale-[1.02]' : 'border-white/10 hover:border-white/30'}`}
              >
                <img src={t.preview} alt={t.name} className="w-full h-14 object-cover" />
                {/* Palette strip */}
                <div className="h-2 flex">
                  {t.palette.map(c => (
                    <div key={c} className="flex-1" style={{ backgroundColor: c }}></div>
                  ))}
                </div>
                <div className="px-2 py-1.5 bg-white/5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-white truncate">{t.name}</span>
                  <div className="flex gap-0.5 flex-shrink-0">
                    {t.palette.slice(0, 3).map(c => (
                      <div key={c} className="w-2 h-2 rounded-full" style={{ backgroundColor: c }}></div>
                    ))}
                  </div>
                </div>
                {selected === t.name && (
                  <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: INFOGRAPHIC_TYPE_COLORS[t.name] ?? '#E63946' }}>
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right — large preview + accent + apply */}
        <div className="space-y-4">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Preview</p>
            <div className="relative rounded-2xl overflow-hidden border border-white/20 aspect-video group">
              <img src={active.preview} alt={selected} className="w-full h-full object-cover" />
              {/* Palette overlay strip at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-3 flex">
                {active.palette.map(c => (
                  <div key={c} className="flex-1 opacity-90" style={{ backgroundColor: c }}></div>
                ))}
              </div>
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => setLightboxTheme(active)}
                  className="flex items-center gap-2 px-4 py-2 bg-white/95 rounded-xl text-xs font-bold text-slate-900 cursor-pointer hover:bg-white transition-colors whitespace-nowrap"
                >
                  <i className="ri-layout-grid-line text-sm"></i>Preview All 12 Slide Types
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-white">{selected}</p>
                <div className="flex gap-1">
                  {active.palette.map(c => (
                    <div key={c} className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: c }}></div>
                  ))}
                </div>
              </div>
              <button
                onClick={() => setLightboxTheme(active)}
                className="flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-eye-line text-xs"></i>See all 12 slides
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Accent Color</p>
            <div className="flex gap-2 flex-wrap">
              {ACCENT_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setSelectedColor(c)}
                  className="w-8 h-8 rounded-full border-2 transition-all cursor-pointer hover:scale-110"
                  style={{ backgroundColor: c, borderColor: selectedColor === c ? 'white' : 'transparent' }}
                ></button>
              ))}
            </div>
          </div>

          <button
            onClick={onSignUp}
            className="w-full flex items-center justify-center gap-2 py-2.5 text-white text-sm font-bold rounded-xl cursor-pointer hover:opacity-90 whitespace-nowrap"
            style={{ background: `linear-gradient(135deg, ${selectedColor}, ${active.palette[1] ?? '#2A9D8F'})` }}
          >
            <i className="ri-palette-line"></i>Apply Infograpia Theme — Sign In
          </button>

          {/* Theme info bar */}
          <div className="flex items-center gap-3 px-3 py-2 bg-white/5 border border-white/10 rounded-xl">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: INFOGRAPHIC_TYPE_COLORS[selected] ?? '#E63946' }}></div>
            <p className="text-xs text-slate-400 truncate">
              {themeDef.darkBg ? 'Dark background' : 'Light background'} · {active.palette.length}-color Infograpia palette
            </p>
            <div className="ml-auto flex-shrink-0 flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded-full">
              <i className="ri-award-line text-amber-400 text-xs"></i>
              <span className="text-xs font-bold text-amber-400">Infograpia</span>
            </div>
          </div>
        </div>
      </div>

      <ThemeLightbox
        theme={lightboxTheme}
        onClose={() => setLightboxTheme(null)}
        onApply={() => { if (lightboxTheme) setSelected(lightboxTheme.name); setLightboxTheme(null); onSignUp(); }}
      />
    </>
  );
}
